import { api } from './client';

// tsconfig has erasableSyntaxOnly, so these mirror the backend enums as string literal unions rather than `enum`
export type FarmLogType = 'PLANTING' | 'FERTILIZING' | 'PESTICIDE' | 'IRRIGATION' | 'HARVEST' | 'EXPENSE' | 'OTHER';

export type FarmLogSource = 'APP' | 'ASR_PENDING';

export type FarmLogWarningLevel = 'NONE' | 'CAUTION' | 'CHECK' | 'DANGER';

export type FarmLogIssueSeverity = 'LOW' | 'MEDIUM' | 'HIGH';

export interface FarmLogMaterial {
  name: string;
  supplyItemId?: string;
  quantity: number;
  unit: string;
  unitCostVnd?: number;
  amountVnd?: number;
}

export interface FarmLogAttachment {
  fileId: string;
  type: 'image' | 'video' | 'audio' | 'doc';
  caption?: string;
}

export interface FarmLogGeoPoint {
  type: string;
  coordinates: number[];
}

export interface BackendFarmLog {
  _id: string;
  farmerId: string;
  seasonId?: string;
  fieldId?: string;
  scheduleEventId?: string;
  logType: FarmLogType;
  content: string;
  materialsNote?: string;
  materials: FarmLogMaterial[];
  laborHours?: number;
  issueNote?: string;
  issueSeverity?: FarmLogIssueSeverity;
  attachments: FarmLogAttachment[];
  videoUrl?: string;
  voiceScript?: string;
  gpsLocation?: FarmLogGeoPoint;
  verifiedByField: boolean;
  source: FarmLogSource;
  warningLevel: FarmLogWarningLevel;
  warningTags: string[];
  htxNote?: string;
  isDeleted: boolean;
  deletedReason?: string;
  deletedAt?: string;
  editedAt?: string;
  editCount: number;
  // schema uses timestamps: { createdAt: 'recordedAt', updatedAt: false }, so this is the only timestamp field
  recordedAt: string;
}

export interface CreateFarmLogPayload {
  farmerId: string;
  seasonId?: string;
  fieldId?: string;
  scheduleEventId?: string;
  logType: FarmLogType;
  content: string;
  materialsNote?: string;
  materials?: FarmLogMaterial[];
  laborHours?: number;
  issueNote?: string;
  issueSeverity?: FarmLogIssueSeverity;
  attachments?: FarmLogAttachment[];
  videoUrl?: string;
  voiceScript?: string;
  gpsLocation?: { coordinates: number[] };
}

export interface UpdateFarmLogPayload {
  logType?: FarmLogType;
  content?: string;
  materialsNote?: string;
  materials?: FarmLogMaterial[];
  laborHours?: number;
  issueNote?: string;
  issueSeverity?: FarmLogIssueSeverity;
  attachments?: FarmLogAttachment[];
}

export async function createFarmLog(data: CreateFarmLogPayload): Promise<BackendFarmLog> {
  return api.post<BackendFarmLog>('/farm-logs', data);
}

export async function updateFarmLog(id: string, data: UpdateFarmLogPayload): Promise<BackendFarmLog> {
  return api.patch<BackendFarmLog>(`/farm-logs/${id}`, data);
}

export async function deleteFarmLog(id: string, reason: string): Promise<BackendFarmLog> {
  return api.post<BackendFarmLog>(`/farm-logs/${id}/delete`, { reason });
}

export async function setFarmLogHtxNote(id: string, note: string): Promise<BackendFarmLog> {
  return api.post<BackendFarmLog>(`/farm-logs/${id}/htx-note`, { note });
}

export async function getFarmLogById(id: string): Promise<BackendFarmLog> {
  return api.get<BackendFarmLog>(`/farm-logs/${id}`);
}

export async function listFarmLogsByFarmer(farmerId: string, seasonId?: string): Promise<BackendFarmLog[]> {
  const query = seasonId ? `?seasonId=${encodeURIComponent(seasonId)}` : '';
  return api.get<BackendFarmLog[]>(`/farm-logs/farmers/${farmerId}${query}`);
}
